/**
 * 安全的 Cookie 管理工具
 * 提供加密和安全設置的 Cookie 操作方法，特別適合存放身份驗證相關資料
 */

import Cookies from "js-cookie";
import CryptoJS from "crypto-js";

// 應用程式前綴，用於區分不同應用程式的 Cookie
const APP_PREFIX = "ortix_";

// 預設的 Cookie 選項
const DEFAULT_OPTIONS: Cookies.CookieAttributes = {
  // 預設 7 天過期
  expires: 7,
  // 只允許 HTTPS 傳輸 (生產環境)
  secure: import.meta.env.PROD,
  // 防止 JavaScript 訪問 (增加安全性)
  sameSite: "strict",
  // 限制為當前域名
  path: "/",
};

// 獲取加密密鑰，理想情況下應從環境變數獲取
const getEncryptionKey = (): string => {
  return (
    import.meta.env.VITE_COOKIE_ENCRYPTION_KEY ||
    "default-secure-key-change-in-production"
  );
};

/**
 * 安全 Cookie 工具
 * 提供加密的 Cookie 操作方法
 */
export const secureCookies = {
  /**
   * 設置加密的 Cookie
   * @param key Cookie 鍵名
   * @param value 要儲存的值
   * @param options Cookie 選項 (可選)
   */
  set: (
    key: string,
    value: any,
    options: Cookies.CookieAttributes = {},
  ): void => {
    try {
      // 轉換為字串
      const valueToStore =
        typeof value === "object" ? JSON.stringify(value) : String(value);

      // 加密資料
      const encryptedValue = CryptoJS.AES.encrypt(
        valueToStore,
        getEncryptionKey(),
      ).toString();

      // 合併選項
      const cookieOptions = { ...DEFAULT_OPTIONS, ...options };

      // 儲存到 Cookie，加上前綴
      Cookies.set(`${APP_PREFIX}${key}`, encryptedValue, cookieOptions);
    } catch (error) {
      console.error("Cookie 設置失敗:", error);
    }
  },

  /**
   * 從 Cookie 獲取並解密值
   * @param key Cookie 鍵名
   * @param defaultValue 預設值（如果項目不存在或解密失敗）
   * @returns 解密後的值或預設值
   */
  get: <T>(key: string, defaultValue?: T): T | string | null => {
    try {
      // 從 Cookie 獲取加密的值
      const encryptedValue = Cookies.get(`${APP_PREFIX}${key}`);

      if (!encryptedValue) {
        return defaultValue !== undefined ? defaultValue : null;
      }

      // 解密資料
      const decryptedBytes = CryptoJS.AES.decrypt(
        encryptedValue,
        getEncryptionKey(),
      );

      const decryptedValue = decryptedBytes.toString(CryptoJS.enc.Utf8);

      // 嘗試解析為 JSON，如果失敗則返回原始字串
      try {
        return JSON.parse(decryptedValue);
      } catch {
        return decryptedValue;
      }
    } catch (error) {
      console.error("Cookie 讀取失敗:", error);
      return defaultValue !== undefined ? defaultValue : null;
    }
  },

  /**
   * 移除 Cookie
   * @param key Cookie 鍵名
   * @param options Cookie 選項 (可選)
   */
  remove: (key: string, options: Cookies.CookieAttributes = {}): void => {
    try {
      // 合併選項
      const cookieOptions = { ...DEFAULT_OPTIONS, ...options };

      // 移除 Cookie
      Cookies.remove(`${APP_PREFIX}${key}`, cookieOptions);
    } catch (error) {
      console.error("Cookie 移除失敗:", error);
    }
  },

  /**
   * 檢查 Cookie 是否存在
   * @param key Cookie 鍵名
   * @returns 是否存在
   */
  has: (key: string): boolean => {
    return Cookies.get(`${APP_PREFIX}${key}`) !== undefined;
  },

  /**
   * 設置身份驗證相關 Cookie
   * 特別為身份驗證設計的方法，使用更嚴格的安全設置
   * @param key Cookie 鍵名
   * @param value 要儲存的值
   * @param options Cookie 選項 (可選)
   */
  setAuth: (
    key: string,
    value: string,
    options: Cookies.CookieAttributes = {},
  ): void => {
    const authOptions: Cookies.CookieAttributes = {
      // 預設 1 天過期 (較短的有效期)
      expires: 1,
      // 只允許 HTTPS 傳輸
      secure: true,
      // 防止 CSRF 攻擊
      sameSite: "strict",
      // 限制為當前域名
      path: "/",
      // 合併自定義選項
      ...options,
    };

    secureCookies.set(key, value, authOptions);
  },

  /**
   * 移除所有身份驗證相關 Cookie
   * 用於登出功能
   * @param authKeys 身份驗證相關的 Cookie 鍵名陣列
   */
  clearAuth: (authKeys: string[]): void => {
    authKeys.forEach((key) => secureCookies.remove(key));
  },
};
